import { patchCompany } from "api/company.api";
import SecondaryButton from "components/buttons/SecondaryButton";
import Modal from "components/Modal";
import { FC, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Company, OperatorPrivileges } from "types/company.type";
import { EditOperatorRightsModal } from "./EditOperatorRightsModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CompanyQueryKey } from "api/company.api";
import { useNotification } from "hooks/useNotification";
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  MinusCircleIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { Card, CardBody, CardHeader } from "./ui";
import { cn } from "helpers/classNames";

interface Props {
  company: Company;
}

const PermissionRow = ({
  label,
  granted,
}: {
  label: string;
  granted: boolean;
}) => (
  <div
    className={cn(
      "flex items-center gap-3 rounded-xl border p-3.5",
      granted
        ? "border-green-100 bg-green-50/50"
        : "border-slate-200 bg-white",
    )}
  >
    {granted ? (
      <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-600" />
    ) : (
      <MinusCircleIcon className="h-5 w-5 shrink-0 text-slate-300" />
    )}
    <span
      className={cn(
        "text-sm font-medium",
        granted ? "text-slate-900" : "text-slate-400",
      )}
    >
      {label}
    </span>
  </div>
);

export const OperatorRightsTab: FC<Props> = ({ company }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const notification = useNotification();
  const queryClient = useQueryClient();

  const {
    mutate: updateRights,
    isSuccess,
    isPending,
  } = useMutation({
    ...patchCompany(),
    onSuccess: () => {
      notification.info("Rättigheter uppdaterade");
      queryClient.invalidateQueries({
        queryKey: [CompanyQueryKey.GetOne, company.id],
      });
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<OperatorPrivileges>();

  const onSubmit = handleSubmit((data: OperatorPrivileges) => {
    updateRights({
      id: company.id,
      payload: { operatorPrivileges: data },
    });
  });

  const resetForm = useCallback(
    (company: Company) => {
      if (company) {
        reset(company.operatorPrivileges);
      } else {
        reset();
      }
    },
    [reset],
  );

  useEffect(() => {
    if (company) {
      resetForm(company);
    }
  }, [company, resetForm]);

  useEffect(() => {
    if (isSuccess) {
      setIsEditModalOpen(false);
    }
  }, [isSuccess]);

  const onModalClose = () => {
    setIsEditModalOpen(false);
    setTimeout(() => {
      if (company) {
        resetForm(company);
      }
    }, 300);
  };

  const p = company.operatorPrivileges;
  const permissions: { label: string; granted: boolean }[] = [
    { label: "Visa kundlista", granted: !!p.viewCustomerList },
    { label: "Lägg till kund", granted: !!p.addCustomer },
    { label: "Visa kund", granted: !!p.viewCustomer },
    { label: "Redigera kund", granted: !!p.editCustomer },
    { label: "Lägg till anläggning", granted: !!p.addFacility },
    { label: "Redigera anläggning", granted: !!p.editFacility },
    { label: "Lägg till objekt", granted: !!p.addSeparator },
  ];
  const grantedCount = permissions.filter((perm) => perm.granted).length;

  return (
    <>
      <Card>
        <CardHeader
          icon={ShieldCheckIcon}
          title="Operatörsrättigheter"
          description={`${grantedCount} av ${permissions.length} rättigheter aktiva för operatörer.`}
          accent="violet"
          action={
            <SecondaryButton
              size="small"
              onClick={() => setIsEditModalOpen(true)}
            >
              <PencilSquareIcon className="mr-1.5 h-4 w-4" />
              Ändra
            </SecondaryButton>
          }
        />
        <CardBody>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {permissions.map((perm) => (
              <PermissionRow
                key={perm.label}
                label={perm.label}
                granted={perm.granted}
              />
            ))}
          </div>
        </CardBody>
      </Card>
      <Modal
        isOpen={isEditModalOpen}
        onClose={onModalClose}
        title="Ändra operatörsrättigheter"
        actionText="Spara ändringar"
        actionHandler={onSubmit}
        isLoading={isPending}
        loadingText="Sparar ändringar..."
      >
        <EditOperatorRightsModal
          register={register}
          errors={errors}
          control={control}
        />
      </Modal>
    </>
  );
};
